import { Link } from 'react-router';
import { Tag } from '@/components/Tag';
import { daysUntil, num, qty, shortDate } from '@/lib/format';
import { signedQuantity, transactionKind } from './status';
import type { InventoryTransaction } from './types';
import s from './TxCards.module.css';

function pad(n: number) {
  return String(n).padStart(2, '0');
}
export function hhmm(v: string) {
  const d = new Date(v);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function dayLabel(v: string) {
  const d = daysUntil(v);
  const md = shortDate(v);
  if (d === 0) return `${md} 今天`;
  if (d === -1) return `${md} 昨天`;
  const w = '日一二三四五六'[new Date(v).getDay()];
  return `${md} 周${w}`;
}

/** 带符号的数量：入库绿 +、出库红 −，预占 / 释放不动实物、不带符号 */
export function TxQty({ t }: { t: InventoryTransaction }) {
  const q = signedQuantity(t);
  return <b className={`tnum ${s[q.tone]}`}>{q.sign}{qty(q.value)}</b>;
}

export function TxAvailable({ t }: { t: InventoryTransaction }) {
  if (t.availableAfter == null) return <span className="em" title="这笔流水的历史可用库存无法可靠还原">—</span>;
  return <span className={`tnum ${num(t.availableAfter) < 0 ? s.neg : ''}`}>{qty(t.availableAfter)}</span>;
}

export function hasSource(t: InventoryTransaction) {
  return !!(t.sourceShipment || t.sourceOrder || t.sourcePurchaseOrder || t.sourceTaobaoOrder || t.referenceType === 'InitialStockImport' || t.referenceType === 'OrderException' || (t.type === 'ADJUST' && t.note));
}

/** 来源单据：订单、发货批次进 v2 订单详情；采购单进 v2 采购单详情；淘宝店还在老版 */
export function TxSource({ t, className }: { t: InventoryTransaction; className?: string }) {
  const cls = [s.src, className].filter(Boolean).join(' ');
  if (t.sourceShipment) {
    const text = `${t.sourceShipment.orderNo || ''} · ${t.sourceShipment.shipmentNo}`;
    return t.sourceOrder ? <Link className={cls} to={`/orders/${t.sourceOrder.id}`} onClick={(e) => e.stopPropagation()}>{text}</Link> : <span className={cls}>{text}</span>;
  }
  if (t.sourceOrder) return <Link className={cls} to={`/orders/${t.sourceOrder.id}`} onClick={(e) => e.stopPropagation()}>{t.sourceOrder.orderNo}</Link>;
  if (t.sourcePurchaseOrder) return <Link className={cls} to={`/purchase-orders/${t.sourcePurchaseOrder.id}`} onClick={(e) => e.stopPropagation()}>{t.sourcePurchaseOrder.purchaseNo}</Link>;
  if (t.sourceTaobaoOrder) {
    return (
      <Link className={cls} to={`/taobao/${t.sourceTaobaoOrder.id}`}>
        {t.sourceTaobaoOrder.orderNo}{t.sourceTaobaoReturn ? ` / ${t.sourceTaobaoReturn.returnNo}` : ''}
      </Link>
    );
  }
  if (t.referenceType === 'InitialStockImport') return <span className={`${cls} ${s.plain}`}>期初库存导入</span>;
  if (t.referenceType === 'OrderException') return <span className={`${cls} ${s.plain}`}>关联异常单</span>;
  if (t.type === 'ADJUST' && t.note) return <span className={`${cls} ${s.plain}`} title={t.note}>{t.note}</span>;
  return null;
}

/** 手机 / iPad 卡片模式（L2）：按天分组，一天一张白底块，行内「产品 + 类型｜数量」 */
export function TxList({ items }: { items: InventoryTransaction[] }) {
  const groups: { day: string; label: string; rows: InventoryTransaction[] }[] = [];
  for (const t of items) {
    const day = new Date(t.createdAt).toDateString();
    const g = groups[groups.length - 1];
    if (g && g.day === day) g.rows.push(t);
    else groups.push({ day, label: dayLabel(t.createdAt), rows: [t] });
  }
  return (
    <>
      {groups.map((g) => (
        <section key={g.day} className={s.day}>
          <h3>{g.label}</h3>
          <ul className={s.card}>
            {g.rows.map((t) => {
              const kind = transactionKind(t);
              const src = hasSource(t);
              return (
                <li key={t.id}>
                  <div className={s.main}>
                    <div className={s.t}>
                      <b>{t.product.name}</b>
                      <Tag tone={kind.tone} sm>{kind.label}</Tag>
                    </div>
                    <div className={s.meta}>
                      <span className="tnum">{t.product.code}</span> · {t.warehouse.name} · <span className="tnum">{hhmm(t.createdAt)}</span>
                      {t.createdBy && <> · {t.createdBy.name}</>}
                    </div>
                    {src && <TxSource t={t} />}
                    {t.note && !src && <p className={s.note}>{t.note}</p>}
                  </div>
                  <div className={s.q}>
                    <TxQty t={t} />
                    <small>变动后可用 <TxAvailable t={t} /></small>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </>
  );
}
